import { useState } from 'react';
import { X, BookOpen, BarChart3, Package, Settings, Chrome, Database, AlertCircle } from 'lucide-react';

export default function HelpModal({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('overview');

    if (!isOpen) return null;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'data', label: 'Understanding Data', icon: Database },
        { id: 'products', label: 'Products', icon: Package },
        { id: 'settings', label: 'Settings', icon: Settings },
        { id: 'extension', label: 'Extension', icon: Chrome },
        { id: 'troubleshooting', label: 'Troubleshooting', icon: AlertCircle },
    ];

    const content = {
        overview: (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Overview Dashboard</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        The Overview page shows real-time analytics of edtech usage across your district.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Key Metrics</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Total Visits:</strong> Number of times students/staff accessed edtech tools</li>
                        <li><strong>Unique Products:</strong> Different tools being used</li>
                        <li><strong>Active Devices:</strong> Chromebooks that have reported activity</li>
                        <li><strong>Categories:</strong> Product categories in use</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Filters</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Date Range:</strong> View data for specific time periods</li>
                        <li><strong>Campus:</strong> Filter by individual schools</li>
                        <li><strong>User Type:</strong> Separate staff and student usage</li>
                        <li><strong>Category:</strong> Focus on specific tool categories</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Charts</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Heatmap:</strong> Shows usage patterns by hour and day of week</li>
                        <li><strong>Top Products:</strong> Most frequently used tools</li>
                        <li><strong>Category Distribution:</strong> Usage breakdown by category</li>
                        <li><strong>Trend Chart:</strong> Usage over time</li>
                    </ul>
                </div>
            </div>
        ),

        data: (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Understanding Your Data</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        Learn how EdFrame collects and organizes your edtech usage data.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">How Data is Collected</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        The EdFrame Chrome extension runs on student and staff Chromebooks. When a user visits a website, the extension:
                    </p>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400 ml-4">
                        <li>Captures the domain (e.g., classroom.google.com)</li>
                        <li>Sends it to EdFrame with a timestamp</li>
                        <li>EdFrame matches it to known edtech products</li>
                        <li>Categorizes and displays it in your dashboard</li>
                    </ol>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">User Types</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Student:</strong> Chromebooks configured with student token - shows student usage patterns</li>
                        <li><strong>Staff:</strong> Chromebooks configured with staff token - shows teacher/admin usage</li>
                    </ul>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        Separating user types helps you understand different usage patterns and make informed decisions about tool adoption.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Campus Segmentation</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Each campus has unique tokens, allowing you to track usage by individual schools. This helps identify which campuses are using which tools most effectively.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Data Privacy</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        EdFrame only collects domain names and timestamps. We do NOT collect:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400 ml-4">
                        <li>Page content or what users are viewing</li>
                        <li>Keystrokes or form data</li>
                        <li>Personal information</li>
                        <li>Browsing history outside of edtech tools</li>
                    </ul>
                </div>
            </div>
        ),

        products: (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Product Management</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        Manage your edtech product catalog and categories.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Adding Products</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Click "Add Product" to manually add a new edtech tool. Provide the product name, domain pattern (e.g., *.google.com), and category.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Review Tab (Pending Domains)</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        When users visit unknown domains, they appear in the "Review" tab. You have three options:
                    </p>
                    <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400 ml-4">
                        <li><strong>Approve:</strong> Add the domain as a recognized edtech product</li>
                        <li><strong>Deny:</strong> Block the domain from appearing in analytics</li>
                        <li><strong>Ignore:</strong> Remove from review without approving or denying</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Ignored Domains</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        Domains you've chosen to ignore appear in the "Ignored" tab. These are domains that aren't edtech tools but you don't want to block. Examples: Google search, YouTube, news sites.
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                        From the Ignored tab, you can:
                    </p>
                    <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400 ml-4">
                        <li><strong>Move to Pending:</strong> Send back to review if you change your mind</li>
                        <li><strong>Delete:</strong> Permanently remove the domain from the system</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Categories</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Organize products into categories (e.g., LMS, Assessment, Productivity) to better understand tool usage patterns.
                    </p>
                </div>
            </div>
        ),

        settings: (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Settings</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        Manage users, organizations, and campuses.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">User Roles</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Admin:</strong> Full access - can manage users, settings, and view all data</li>
                        <li><strong>Viewer:</strong> Read-only access - can view analytics but cannot make changes</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Tokens</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Organization Token:</strong> District-wide token (legacy - use campus tokens instead)</li>
                        <li><strong>Staff Token:</strong> Configure on staff/teacher Chromebooks</li>
                        <li><strong>Student Token:</strong> Configure on student Chromebooks</li>
                    </ul>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        Tokens are hidden by default for security. Click the eye icon to reveal, then copy to configure the Chrome extension.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Token Security</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Keep tokens secure. If compromised, regenerate them immediately. Note: regenerating a token will stop data collection from devices using the old token.
                    </p>
                </div>
            </div>
        ),

        extension: (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Chrome Extension</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        Install and configure the EdFrame extension on Chromebooks.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Installation</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400 ml-4">
                        <li>Deploy via Google Admin Console (recommended)</li>
                        <li>Configure the organization token in extension settings</li>
                        <li>Extension will automatically start collecting data</li>
                    </ol>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Configuration</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Use campus-specific tokens (staff or student) to properly segment your data. Configure different tokens for different organizational units in Google Admin.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Troubleshooting</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>No data appearing:</strong> Verify token is configured correctly in extension</li>
                        <li><strong>Wrong user type:</strong> Check if correct token (staff vs student) is deployed</li>
                        <li><strong>Missing devices:</strong> Ensure extension is installed and enabled</li>
                    </ul>
                </div>
            </div>
        ),

        troubleshooting: (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">Troubleshooting</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        Common issues and solutions.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">No Data Showing</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li>✓ Verify extension is installed on Chromebooks</li>
                        <li>✓ Check token is configured in extension settings</li>
                        <li>✓ Ensure organization/campus is active in Settings</li>
                        <li>✓ Try a different date range filter</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Rate Limit Errors</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        If you see "Too many requests" errors, wait a few minutes before trying again. This protects the system from overload.
                    </p>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Token Issues</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Token not working:</strong> Verify you copied the entire token (no spaces)</li>
                        <li><strong>Data stopped coming in:</strong> Check if token was regenerated</li>
                        <li><strong>Wrong user type:</strong> Verify correct token (staff vs student) is deployed</li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">Charts Not Loading</h4>
                    <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                        <li>✓ Check if data exists for selected filters</li>
                        <li>✓ Try refreshing the page</li>
                        <li>✓ Clear browser cache</li>
                        <li>✓ Try a different browser</li>
                    </ul>
                </div>
            </div>
        ),
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-surface-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-surface-700">
                    <div className="flex items-center gap-2">
                        <BookOpen className="text-blue-600 dark:text-blue-400" size={24} />
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">EdFrame Help</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-surface-700 rounded-lg transition-colors"
                        aria-label="Close help"
                    >
                        <X size={20} className="text-slate-500 dark:text-slate-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Sidebar */}
                    <div className="w-64 border-r border-slate-200 dark:border-surface-700 p-4 overflow-y-auto">
                        <nav className="space-y-1">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-surface-700'
                                            }`}
                                    >
                                        <Icon size={18} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 p-6 overflow-y-auto">
                        {content[activeTab]}
                    </div>
                </div>
            </div>
        </div>
    );
}
